import { maskMyNumber } from '../GeneralProfile/useGeneralProfileFlow';
import {
  changeReasonLabelKey,
  isSpouseChangeReasonCode,
} from '../dependentDeduction/spouseSection/spouseChangeReason';
import { needsDisabilityFacts } from '../disabilityFacts';
import { formatOtherIncomeChangeReasonDisplay } from '../dependentDeduction/otherIncomeEarner/otherIncomeEarnerDisplay';
import { disabilityLabel } from '../dependentDeduction/spouseSection/spouseSectionDisplay';
import { isSpouseTaxFlow } from './familyInfoSpouseEligibility';
import {
  ALL_TAX_DEPENDENT_CATEGORY_KANJI,
  deriveDependentIneligibility,
  incomeOverDependentLimit,
  isDependentChangeReasonCode,
  isDependentNonResidentReasonCode,
  isNonSpouseTaxFlow,
  isUnder16AtTaxYearEnd,
  taxDependentCategoryLabel,
} from './familyInfoDependentEligibility';
import type { DisabilityCategory, FamilyMemberDraft, FamilyStep } from './familyInfoTypes';

function isSpouseMember(draft: FamilyMemberDraft): boolean {
  return draft.relationship === 'spouse';
}

/** Skip the annual-income question for members under 16 at tax year-end. */
export function skipsEstimatedIncomeQuestion(draft: FamilyMemberDraft): boolean {
  return isUnder16AtTaxYearEnd(draft.dateOfBirth, draft.taxYear);
}

function taxBranchStepKey(step: string, draft?: FamilyMemberDraft): string {
  const base =
    draft && isSpouseTaxFlow(draft)
      ? 'submitDocuments.dependentDeduction.spouse.steps'
      : 'submitDocuments.dependentDeduction.dependents.steps';
  return `${base}.${step}`;
}

export function stepQuestionKey(step: FamilyStep, draft?: FamilyMemberDraft): string {
  if (step === 'estimatedIncome') {
    if (draft && isSpouseTaxFlow(draft)) {
      return 'submitDocuments.dependentDeduction.spouse.steps.spouseEstimatedIncome';
    }
    return 'submitDocuments.dependentDeduction.dependents.steps.estimatedIncome';
  }
  if (step === 'spouseFamilyWorker') {
    return 'submitDocuments.dependentDeduction.spouse.steps.spouseFamilyWorker';
  }
  if (step === 'sharedLivelihood') {
    return taxBranchStepKey('sharedLivelihood', draft);
  }
  if (
    step === 'disabilityStatus' ||
    step === 'disabilityFacts' ||
    step === 'changeDuringYear' ||
    step === 'changeDate' ||
    step === 'changeReason' ||
    step === 'changeReasonOther'
  ) {
    return taxBranchStepKey(step, draft);
  }
  if (step === 'taxDependentCategory') {
    return 'submitDocuments.familyInfo.steps.taxDependentCategory';
  }
  if (step === 'taxNonResident') {
    return 'submitDocuments.familyInfo.steps.taxNonResident';
  }
  if (step === 'dependentIneligibleNotice') {
    return 'submitDocuments.familyInfo.steps.dependentIneligibleNotice';
  }
  if (step === 'nonResidentReasonOther') {
    return 'submitDocuments.familyInfo.steps.nonResidentReasonOther';
  }
  return `submitDocuments.familyInfo.steps.${step}`;
}

export function stepHelpKey(step: FamilyStep, draft?: FamilyMemberDraft): string {
  if (draft && isSpouseTaxFlow(draft) && (step === 'sharedLivelihood' || step === 'spouseFamilyWorker')) {
    return `submitDocuments.dependentDeduction.spouse.help.${step}`;
  }
  if (step === 'sharedLivelihood') {
    return 'submitDocuments.dependentDeduction.dependents.help.sharedLivelihood';
  }
  if (step === 'estimatedIncome') {
    return draft && isSpouseTaxFlow(draft)
      ? 'submitDocuments.dependentDeduction.spouse.help.spouseEstimatedIncome'
      : 'submitDocuments.dependentDeduction.dependents.help.estimatedIncome';
  }
  if (step === 'taxNonResident') {
    return 'submitDocuments.dependentDeduction.dependents.help.nonResident';
  }
  if (step === 'remittanceAmount') {
    return 'submitDocuments.dependentDeduction.dependents.help.remittanceAmount';
  }
  return `submitDocuments.familyInfo.help.${step}`;
}

/** Steps that show contextual guidance above the composer (tax/insurance rules). */
export function hasHelpText(step: FamilyStep): boolean {
  return [
    'sameHousehold',
    'outsideJapan',
    'taxGate',
    'sharedLivelihood',
    'spouseFamilyWorker',
    'estimatedIncome',
    'taxDependentCategory',
    'taxNonResident',
    'nonResidentReason',
    'remittanceAmount',
    'disabilityStatus',
    'disabilityFacts',
    'myNumber',
    'insuranceRequired',
    'myNumberLink',
    'supportedDependent',
    'otherIncomeGate',
    'changeDuringYear',
    'changeDate',
    'changeReason',
  ].includes(step);
}

function clearSpouseTaxFields(next: FamilyMemberDraft): void {
  next.sharedLivelihood = '';
  next.spouseFamilyWorker = '';
  next.disabilityCategory = '';
  next.disabilityFacts = '';
  next.changeDuringYear = '';
  next.withholdingSpouse = '';
}

/** Clear spouse tax answers for a full re-entry walk (e.g. tax gate no → yes on edit). */
export function clearSpouseTaxReentryFields(d: FamilyMemberDraft): FamilyMemberDraft {
  const next: FamilyMemberDraft = { ...d };
  clearSpouseTaxFields(next);
  next.estimatedIncome = '';
  next.taxChangeDate = '';
  next.taxChangeReason = '';
  next.taxEligible = '';
  return next;
}

const SPOUSE_TAX_WALK_LIMIT = 24;

function spouseTaxStepHasAnswer(step: FamilyStep, draft: FamilyMemberDraft): boolean {
  switch (step) {
    case 'sharedLivelihood':
      return draft.sharedLivelihood === 'yes' || draft.sharedLivelihood === 'no';
    case 'spouseFamilyWorker':
      return draft.spouseFamilyWorker === 'yes' || draft.spouseFamilyWorker === 'no';
    case 'estimatedIncome':
      if (skipsEstimatedIncomeQuestion(draft)) return true;
      return Boolean(draft.estimatedIncome.trim());
    case 'disabilityStatus':
      return Boolean(draft.disabilityCategory);
    case 'disabilityFacts':
      return Boolean(draft.disabilityFacts.trim());
    case 'changeDuringYear':
      return draft.changeDuringYear === 'yes' || draft.changeDuringYear === 'no';
    case 'changeDate':
      return Boolean(draft.taxChangeDate.trim());
    case 'changeReason':
      if (!draft.taxChangeReason.trim()) return false;
      if (draft.taxChangeReason === 'other') return false;
      return isSpouseChangeReasonCode(draft.taxChangeReason);
    case 'changeReasonOther':
      return Boolean(draft.taxChangeReason.trim()) && draft.taxChangeReason !== 'other';
    default:
      return true;
  }
}

/**
 * First spouse tax step still missing answers, or `insuranceRequired` when the tax branch is complete.
 * Used when re-entering the tax flow after tax gate flips no → yes during expanded review edit.
 */
export function firstMissingSpouseTaxStepFrom(start: FamilyStep, draft: FamilyMemberDraft): FamilyStep {
  if (!isSpouseTaxFlow(draft) || draft.claimTaxDependent !== 'yes') {
    return 'insuranceRequired';
  }
  let s = start;
  for (let i = 0; i < SPOUSE_TAX_WALK_LIMIT; i++) {
    if (s === 'insuranceRequired') return s;
    if (!spouseTaxStepHasAnswer(s, draft)) return s;
    s = nextStepAfter(s, draft);
  }
  return 'insuranceRequired';
}

function clearNonSpouseTaxFields(next: FamilyMemberDraft): void {
  next.sharedLivelihood = '';
  next.taxDependentCategory = '';
  next.taxNonResident = '';
  next.taxIneligibilityReason = '';
  next.nonResidentReason = '';
  next.remittanceAmount = '';
  next.disabilityCategory = '';
  next.disabilityFacts = '';
  next.changeDuringYear = '';
  next.taxChangeDate = '';
  next.taxChangeReason = '';
  next.estimatedIncome = '';
  next.withholdingSpouse = '';
  next.taxEligible = '';
}

/** Clear non-spouse tax answers for full re-entry (tax gate no → yes on edit). */
export function clearNonSpouseTaxReentryFields(d: FamilyMemberDraft): FamilyMemberDraft {
  const next: FamilyMemberDraft = { ...d };
  clearNonSpouseTaxFields(next);
  return next;
}

const NON_SPOUSE_TAX_WALK_LIMIT = 24;

function nonSpouseTaxStepHasAnswer(step: FamilyStep, draft: FamilyMemberDraft): boolean {
  switch (step) {
    case 'sharedLivelihood':
      return draft.sharedLivelihood === 'yes' || draft.sharedLivelihood === 'no';
    case 'estimatedIncome':
      if (skipsEstimatedIncomeQuestion(draft)) return true;
      return Boolean(draft.estimatedIncome.trim());
    case 'taxDependentCategory':
      return ALL_TAX_DEPENDENT_CATEGORY_KANJI.includes(
        draft.taxDependentCategory as (typeof ALL_TAX_DEPENDENT_CATEGORY_KANJI)[number]
      );
    case 'taxNonResident':
      return draft.taxNonResident === 'yes' || draft.taxNonResident === 'no';
    case 'nonResidentReason':
      if (!draft.nonResidentReason.trim()) return false;
      if (draft.nonResidentReason === 'none') return false;
      return isDependentNonResidentReasonCode(draft.nonResidentReason);
    case 'nonResidentReasonOther':
      return Boolean(draft.nonResidentReason.trim()) && draft.nonResidentReason !== 'none';
    case 'remittanceAmount':
      return Boolean(draft.remittanceAmount.trim());
    case 'disabilityStatus':
      return Boolean(draft.disabilityCategory);
    case 'disabilityFacts':
      return Boolean(draft.disabilityFacts.trim());
    case 'changeDuringYear':
      return draft.changeDuringYear === 'yes' || draft.changeDuringYear === 'no';
    case 'changeDate':
      return Boolean(draft.taxChangeDate.trim());
    case 'changeReason':
      if (!draft.taxChangeReason.trim()) return false;
      if (draft.taxChangeReason === 'other') return false;
      return isDependentChangeReasonCode(draft.taxChangeReason) || isSpouseChangeReasonCode(draft.taxChangeReason);
    case 'changeReasonOther':
      return Boolean(draft.taxChangeReason.trim()) && draft.taxChangeReason !== 'other';
    case 'dependentIneligibleNotice':
      return draft.insuranceRequired === 'yes' || draft.insuranceRequired === 'no';
    default:
      return true;
  }
}

export function firstMissingNonSpouseTaxStepFrom(
  start: FamilyStep,
  draft: FamilyMemberDraft
): FamilyStep {
  if (!isNonSpouseTaxFlow(draft)) return 'insuranceRequired';
  if (draft.taxIneligibilityReason) {
    return draft.insuranceRequired === 'yes' || draft.insuranceRequired === 'no'
      ? 'insuranceRequired'
      : 'dependentIneligibleNotice';
  }
  let s = start;
  for (let i = 0; i < NON_SPOUSE_TAX_WALK_LIMIT; i++) {
    if (s === 'insuranceRequired' || s === 'dependentIneligibleNotice') return s;
    if (s === 'dependentIneligibleNotice') return s;
    if (!nonSpouseTaxStepHasAnswer(s, draft)) return s;
    s = nextStepAfter(s, draft);
  }
  return 'insuranceRequired';
}

/** Clear branch-only answers when a gate answer closes a path. */
export function finalizeStepDraft(fromStep: FamilyStep, d: FamilyMemberDraft): FamilyMemberDraft {
  const next: FamilyMemberDraft = { ...d };
  switch (fromStep) {
    case 'relationship':
      if (next.relationship !== 'spouse') clearSpouseTaxFields(next);
      if (next.relationship === 'spouse') clearNonSpouseTaxFields(next);
      break;
    case 'sameHousehold':
      if (next.livingInSameHousehold === 'yes') {
        next.ifNotAddress = '';
        next.outsideJapan = '';
        if (!isSpouseTaxFlow(next)) next.nonResidentFlag = false;
      }
      break;
    case 'taxGate':
      if (next.claimTaxDependent === 'no') {
        clearSpouseTaxFields(next);
        clearNonSpouseTaxFields(next);
        next.estimatedIncome = '';
        next.taxIneligibilityReason = '';
      } else if (isSpouseTaxFlow(next)) {
        clearNonSpouseTaxFields(next);
      } else {
        clearSpouseTaxFields(next);
      }
      break;
    case 'sharedLivelihood':
      if (!isSpouseTaxFlow(next) && next.sharedLivelihood === 'no') {
        const sl = next.sharedLivelihood;
        clearNonSpouseTaxFields(next);
        next.sharedLivelihood = sl;
        next.taxIneligibilityReason = 'no_shared_livelihood';
        next.taxEligible = 'no';
      } else if (!isSpouseTaxFlow(next)) {
        next.taxIneligibilityReason = '';
        if (skipsEstimatedIncomeQuestion(next)) {
          next.estimatedIncome = '0';
        }
      }
      break;
    case 'spouseFamilyWorker':
      if (skipsEstimatedIncomeQuestion(next)) {
        next.estimatedIncome = '0';
      }
      break;
    case 'estimatedIncome':
      if (!isSpouseTaxFlow(next) && incomeOverDependentLimit(next.estimatedIncome, next.dateOfBirth, next.taxYear)) {
        next.taxIneligibilityReason = 'income_over_limit';
        next.taxEligible = 'no';
      } else if (!isSpouseTaxFlow(next) && !next.taxIneligibilityReason) {
        next.taxEligible = '';
      }
      break;
    case 'nonResidentReasonOther':
      if (
        !isSpouseTaxFlow(next) &&
        deriveDependentIneligibility(next, next.taxYear) === 'non_resident_no_support'
      ) {
        next.taxIneligibilityReason = 'non_resident_no_support';
        next.taxEligible = 'no';
      }
      break;
    case 'insuranceRequired':
      if (next.insuranceRequired === 'no') {
        next.myNumberLinkStatus = '';
        next.supportedDependent = '';
      }
      break;
    case 'nonResidentReason':
      if (next.nonResidentReason !== 'remittance_380k') next.remittanceAmount = '';
      if (next.nonResidentReason !== 'none') {
        /* keep free text when set via other step */
      }
      break;
    case 'taxNonResident':
      if (next.taxNonResident === 'no') {
        next.nonResidentReason = '';
        next.remittanceAmount = '';
      }
      break;
    case 'disabilityStatus':
      if (!needsDisabilityFacts(next.disabilityCategory)) {
        next.disabilityFacts = '';
      }
      break;
    case 'changeDuringYear':
      if (next.changeDuringYear === 'no') {
        next.taxChangeDate = '';
        next.taxChangeReason = '';
      }
      break;
    case 'changeReason':
      if (next.taxChangeReason !== 'other') {
        /* keep coded reason */
      }
      break;
    default:
      break;
  }
  return next;
}

/** My Number may only be collected when there is a tax or insurance purpose. */
export function myNumberStepNeeded(draft: FamilyMemberDraft): boolean {
  return draft.claimTaxDependent === 'yes' || draft.insuranceRequired === 'yes';
}

function spouseTaxStepBeforeInsurance(draft: FamilyMemberDraft): FamilyStep {
  if (draft.changeDuringYear === 'yes') {
    if (draft.taxChangeReason.trim()) {
      if (draft.taxChangeReason === 'other' || !isSpouseChangeReasonCode(draft.taxChangeReason)) {
        return 'changeReasonOther';
      }
      return 'changeReason';
    }
    if (draft.taxChangeDate.trim()) return 'changeDate';
    return 'changeDuringYear';
  }
  if (draft.changeDuringYear === 'no') return 'changeDuringYear';
  if (needsDisabilityFacts(draft.disabilityCategory) && draft.disabilityFacts.trim()) return 'disabilityFacts';
  if (draft.disabilityCategory) return 'disabilityStatus';
  if (draft.estimatedIncome.trim()) return 'estimatedIncome';
  if (draft.spouseFamilyWorker) return 'spouseFamilyWorker';
  if (draft.sharedLivelihood) return 'sharedLivelihood';
  return 'taxGate';
}

function nonSpouseTaxStepBeforeInsurance(draft: FamilyMemberDraft): FamilyStep {
  if (draft.taxIneligibilityReason) return 'dependentIneligibleNotice';
  if (draft.changeDuringYear === 'yes') {
    if (draft.taxChangeReason.trim()) {
      if (draft.taxChangeReason === 'other' || !isDependentChangeReasonCode(draft.taxChangeReason)) {
        return 'changeReasonOther';
      }
      return 'changeReason';
    }
    if (draft.taxChangeDate.trim()) return 'changeDate';
    return 'changeDuringYear';
  }
  if (draft.changeDuringYear === 'no') return 'changeDuringYear';
  if (needsDisabilityFacts(draft.disabilityCategory) && draft.disabilityFacts.trim()) return 'disabilityFacts';
  if (draft.disabilityCategory) return 'disabilityStatus';
  if (draft.taxNonResident === 'yes') {
    if (draft.nonResidentReason === 'remittance_380k' && draft.remittanceAmount.trim()) {
      return 'remittanceAmount';
    }
    if (draft.nonResidentReason.trim() && draft.nonResidentReason !== 'none') {
      return draft.nonResidentReason === 'remittance_380k' ? 'remittanceAmount' : 'nonResidentReason';
    }
    if (draft.nonResidentReason === 'none' || !isDependentNonResidentReasonCode(draft.nonResidentReason)) {
      return 'nonResidentReasonOther';
    }
    return 'nonResidentReason';
  }
  if (draft.taxNonResident === 'no') return 'taxNonResident';
  if (draft.taxDependentCategory.trim()) return 'taxDependentCategory';
  if (draft.estimatedIncome.trim()) return 'estimatedIncome';
  if (draft.sharedLivelihood) return 'sharedLivelihood';
  return 'taxGate';
}

/** Last tax step before end-of-member insurance question. */
function stepBeforeInsurance(draft: FamilyMemberDraft): FamilyStep {
  if (draft.claimTaxDependent === 'no') return 'taxGate';
  if (isSpouseTaxFlow(draft)) return spouseTaxStepBeforeInsurance(draft);
  return nonSpouseTaxStepBeforeInsurance(draft);
}

export function nextStepAfter(current: FamilyStep, draft: FamilyMemberDraft): FamilyStep {
  switch (current) {
    case 'intro':
      return 'hasMembers';
    case 'hasMembers':
      return 'relationship';
    case 'relationship':
      return 'fullName';
    case 'fullName':
      return 'fullNameKana';
    case 'fullNameKana':
      return 'dob';
    case 'dob':
      return 'sameHousehold';
    case 'sameHousehold':
      return draft.livingInSameHousehold === 'yes' ? 'taxGate' : 'ifNotAddress';
    case 'ifNotAddress':
      return isSpouseMember(draft) ? 'outsideJapan' : 'taxGate';
    case 'outsideJapan':
      return 'taxGate';
    case 'insuranceGate':
      if (draft.registerHealthInsurance === 'no') return 'taxGate';
      return 'insuranceRequired';
    case 'insuranceRequired':
      if (draft.insuranceRequired === 'no') {
        return draft.claimTaxDependent === 'yes' ? 'myNumber' : 'addAnother';
      }
      return 'myNumberLink';
    case 'myNumberLink':
      return 'supportedDependent';
    case 'supportedDependent':
      return 'myNumber';
    case 'myNumber':
      return 'addAnother';
    case 'otherIncomeGate':
      return draft.claimedByOtherIncome === 'yes' ? 'otherEarnerName' : 'taxGate';
    case 'otherEarnerName':
      return 'otherEarnerAddress';
    case 'otherEarnerAddress':
      return 'taxGate';
    case 'taxGate':
      if (draft.claimTaxDependent === 'no') return 'insuranceRequired';
      return 'sharedLivelihood';
    case 'sharedLivelihood':
      if (isSpouseTaxFlow(draft)) return 'spouseFamilyWorker';
      if (draft.sharedLivelihood === 'no') return 'dependentIneligibleNotice';
      if (skipsEstimatedIncomeQuestion(draft)) return 'taxDependentCategory';
      return 'estimatedIncome';
    case 'spouseFamilyWorker':
      if (skipsEstimatedIncomeQuestion(draft)) return 'disabilityStatus';
      return 'estimatedIncome';
    case 'estimatedIncome':
      if (isSpouseTaxFlow(draft)) return 'disabilityStatus';
      if (incomeOverDependentLimit(draft.estimatedIncome, draft.dateOfBirth, draft.taxYear)) {
        return 'dependentIneligibleNotice';
      }
      return 'taxDependentCategory';
    case 'dependentIneligibleNotice':
      return 'insuranceRequired';
    case 'taxDependentCategory':
      return 'taxNonResident';
    case 'taxNonResident':
      return draft.taxNonResident === 'yes' ? 'nonResidentReason' : 'disabilityStatus';
    case 'disabilityStatus':
      return needsDisabilityFacts(draft.disabilityCategory) ? 'disabilityFacts' : 'changeDuringYear';
    case 'disabilityFacts':
      return 'changeDuringYear';
    case 'changeDuringYear':
      return draft.changeDuringYear === 'yes' ? 'changeDate' : 'insuranceRequired';
    case 'changeDate':
      return 'changeReason';
    case 'changeReason':
      return draft.taxChangeReason === 'other' ? 'changeReasonOther' : 'insuranceRequired';
    case 'changeReasonOther':
      return 'insuranceRequired';
    case 'nonResidentReason':
      if (draft.nonResidentReason === 'remittance_380k') return 'remittanceAmount';
      if (draft.nonResidentReason === 'none') return 'nonResidentReasonOther';
      return 'disabilityStatus';
    case 'nonResidentReasonOther':
      if (deriveDependentIneligibility(draft, draft.taxYear) === 'non_resident_no_support') {
        return 'dependentIneligibleNotice';
      }
      return 'disabilityStatus';
    case 'remittanceAmount':
      return 'disabilityStatus';
    case 'addAnother':
      return 'finalConfirm';
    default:
      return current;
  }
}

function prevDisabilityStatusStep(draft: FamilyMemberDraft): FamilyStep {
  if (isSpouseTaxFlow(draft)) {
    return skipsEstimatedIncomeQuestion(draft) ? 'spouseFamilyWorker' : 'estimatedIncome';
  }
  if (draft.taxNonResident === 'yes') {
    if (draft.nonResidentReason === 'remittance_380k') return 'remittanceAmount';
    if (draft.nonResidentReason === 'none' || !isDependentNonResidentReasonCode(draft.nonResidentReason)) {
      return 'nonResidentReasonOther';
    }
    return 'nonResidentReason';
  }
  return 'taxNonResident';
}

export function prevStepForUndo(current: FamilyStep, draft: FamilyMemberDraft): FamilyStep | null {
  switch (current) {
    case 'relationship':
      return 'hasMembers';
    case 'fullName':
      return 'relationship';
    case 'fullNameKana':
      return 'fullName';
    case 'dob':
      return 'fullNameKana';
    case 'sameHousehold':
      return 'dob';
    case 'ifNotAddress':
      return 'sameHousehold';
    case 'outsideJapan':
      return 'ifNotAddress';
    case 'taxGate':
      if (draft.livingInSameHousehold === 'yes') return 'sameHousehold';
      return isSpouseMember(draft) ? 'outsideJapan' : 'ifNotAddress';
    case 'sharedLivelihood':
      return 'taxGate';
    case 'spouseFamilyWorker':
      return 'sharedLivelihood';
    case 'dependentIneligibleNotice':
      if (draft.taxIneligibilityReason === 'no_shared_livelihood') return 'sharedLivelihood';
      if (draft.taxIneligibilityReason === 'income_over_limit') return 'estimatedIncome';
      if (draft.taxIneligibilityReason === 'non_resident_no_support') return 'nonResidentReasonOther';
      return 'sharedLivelihood';
    case 'estimatedIncome':
      if (isSpouseTaxFlow(draft)) return 'spouseFamilyWorker';
      return 'sharedLivelihood';
    case 'taxDependentCategory':
      return skipsEstimatedIncomeQuestion(draft) ? 'sharedLivelihood' : 'estimatedIncome';
    case 'taxNonResident':
      return 'taxDependentCategory';
    case 'nonResidentReason':
      return 'taxNonResident';
    case 'nonResidentReasonOther':
      return 'nonResidentReason';
    case 'remittanceAmount':
      return 'nonResidentReason';
    case 'disabilityStatus':
      return prevDisabilityStatusStep(draft);
    case 'disabilityFacts':
      return 'disabilityStatus';
    case 'changeDuringYear':
      return needsDisabilityFacts(draft.disabilityCategory) ? 'disabilityFacts' : 'disabilityStatus';
    case 'changeDate':
      return 'changeDuringYear';
    case 'changeReason':
      return 'changeDate';
    case 'changeReasonOther':
      return 'changeReason';
    case 'insuranceRequired':
      if (draft.taxIneligibilityReason) return 'dependentIneligibleNotice';
      return stepBeforeInsurance(draft);
    case 'myNumberLink':
      return 'insuranceRequired';
    case 'supportedDependent':
      return 'myNumberLink';
    case 'myNumber':
      if (draft.insuranceRequired === 'yes') {
        if (draft.supportedDependent === 'yes' || draft.supportedDependent === 'no') {
          return 'supportedDependent';
        }
        if (draft.myNumberLinkStatus) return 'myNumberLink';
      }
      return 'insuranceRequired';
    case 'addAnother':
      if (myNumberStepNeeded(draft) && draft.myNumber.trim()) return 'myNumber';
      if (draft.insuranceRequired === 'yes') {
        if (draft.supportedDependent === 'yes' || draft.supportedDependent === 'no') {
          return 'supportedDependent';
        }
        if (draft.myNumberLinkStatus) return 'myNumberLink';
      }
      return 'insuranceRequired';
    case 'finalConfirm':
      return 'addAnother';
    default:
      return null;
  }
}

export function userAnswerForStep(step: FamilyStep, draft: FamilyMemberDraft, t: (k: string) => string): string {
  const yn = (v: string) => (v === 'yes' ? t('submitDocuments.familyInfo.yes') : v === 'no' ? t('submitDocuments.familyInfo.no') : '');
  switch (step) {
    case 'hasMembers':
      return yn('yes');
    case 'relationship':
      return draft.relationship ? t(`submitDocuments.familyInfo.relationship.${draft.relationship}`) : '';
    case 'fullName':
      return draft.fullName.trim();
    case 'fullNameKana':
      return draft.fullNameKana.trim();
    case 'dob':
      return draft.dateOfBirth;
    case 'sameHousehold':
    case 'taxGate':
    case 'insuranceGate':
    case 'healthDependent':
    case 'supportedDependent':
    case 'otherIncomeGate':
    case 'withholdingSpouse':
    case 'sharedLivelihood':
    case 'spouseFamilyWorker':
    case 'changeDuringYear':
    case 'taxNonResident':
      return yn((draft as Record<string, string>)[stepToDraftKey(step)] ?? '');
    case 'insuranceRequired':
      if (draft.insuranceRequired === 'yes') return t('submitDocuments.familyInfo.required');
      if (draft.insuranceRequired === 'no') return t('submitDocuments.familyInfo.notRequired');
      return '';
    case 'myNumberLink':
      return draft.myNumberLinkStatus
        ? t(`submitDocuments.familyInfo.myNumber.${draft.myNumberLinkStatus}`)
        : '';
    case 'myNumber':
      return maskMyNumber(draft.myNumber);
    case 'ifNotAddress':
      return draft.ifNotAddress.trim();
    case 'outsideJapan':
      return yn(draft.outsideJapan);
    case 'otherEarnerName':
      return draft.otherEarnerName.trim();
    case 'otherEarnerAddress':
      return draft.otherEarnerAddress.trim();
    case 'estimatedIncome':
      return draft.estimatedIncome.trim();
    case 'taxDependentCategory':
      return draft.taxDependentCategory.trim()
        ? taxDependentCategoryLabel(draft.taxDependentCategory.trim(), t)
        : '';
    case 'dependentIneligibleNotice':
      if (draft.taxIneligibilityReason) {
        return t(
          `submitDocuments.dependentDeduction.dependents.ineligibleReason.${draft.taxIneligibilityReason}`
        );
      }
      return t('submitDocuments.dependentDeduction.dependents.ineligibleIntro');
    case 'disabilityStatus':
      return disabilityLabel(t, draft.disabilityCategory as DisabilityCategory);
    case 'disabilityFacts':
      return draft.disabilityFacts.trim();
    case 'changeDate':
      return draft.taxChangeDate.trim();
    case 'changeReason':
      if (draft.taxChangeReason.trim() && isSpouseChangeReasonCode(draft.taxChangeReason)) {
        return t(changeReasonLabelKey(draft.taxChangeReason));
      }
      if (draft.taxChangeReason.trim() && isDependentChangeReasonCode(draft.taxChangeReason)) {
        return t(`submitDocuments.dependentDeduction.dependents.changeReason.option.${draft.taxChangeReason}`);
      }
      return formatOtherIncomeChangeReasonDisplay(t, draft.taxChangeReason);
    case 'changeReasonOther':
      return draft.taxChangeReason.trim();
    case 'nonResidentReason':
      if (isDependentNonResidentReasonCode(draft.nonResidentReason)) {
        return t(
          `submitDocuments.dependentDeduction.dependents.nonResidentReason.option.${draft.nonResidentReason}`
        );
      }
      return draft.nonResidentReason.trim();
    case 'nonResidentReasonOther':
      return draft.nonResidentReason.trim();
    case 'remittanceAmount':
      return draft.remittanceAmount.trim();
    case 'addAnother':
      return yn((draft as unknown as { addAnother?: string }).addAnother ?? '');
    default:
      return '';
  }
}

function stepToDraftKey(step: FamilyStep): keyof FamilyMemberDraft {
  const map: Partial<Record<FamilyStep, keyof FamilyMemberDraft>> = {
    sameHousehold: 'livingInSameHousehold',
    taxGate: 'claimTaxDependent',
    insuranceGate: 'registerHealthInsurance',
    insuranceRequired: 'insuranceRequired',
    healthDependent: 'healthInsuranceDependent',
    supportedDependent: 'supportedDependent',
    otherIncomeGate: 'claimedByOtherIncome',
    withholdingSpouse: 'withholdingSpouse',
    taxNonResident: 'taxNonResident',
    sharedLivelihood: 'sharedLivelihood',
    spouseFamilyWorker: 'spouseFamilyWorker',
    changeDuringYear: 'changeDuringYear',
  };
  return map[step] ?? 'relationship';
}
